import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation, ViewChildren, QueryList, AfterViewInit, HostListener, ElementRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Utilities } from 'src/app/services';
import { CommonRadioData } from 'src/app/components/common/common-radio/common-radio.component.model';

@Component({
  selector: 'app-complete-inspection',
  standalone: false,
  templateUrl: './complete-inspection.component.html',
  styleUrls: ['./complete-inspection.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CompleteInspectionComponent implements OnChanges, AfterViewInit {

  @ViewChildren('sigField') signatureFields!: QueryList<any>;
  @ViewChildren('sigWrapper') signatureWrappers!: QueryList<ElementRef>;

  private _activity: any;
  public compId = Math.random().toString(36).substring(2, 9);

  @Input()
  set activity(value: any) {
    this._activity = value;
    if (value) {
      this.initDynamicForm();
    }
  }

  get activity(): any {
    return this._activity;
  }

  @Output() confirm = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();

  layoutItems: any[] = [];
  flatEntities: any[] = [];
  formValues: { [key: string]: any } = {};
  listDataMap: { [key: string]: CommonRadioData } = {};

  documentDataMap: { [key: string]: any[] } = {};
  uploadingMap: { [key: string]: boolean } = {};

  hasSignature: { [key: string]: boolean } = {};
  isSignatureModified: { [key: string]: boolean } = {}; 

  constructor(
    private http: HttpClient,
    private toastr: ToastrService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activity'] && changes['activity'].currentValue) {
      this.initDynamicForm();
    }
  }

  ngAfterViewInit() {
    this.signatureFields.changes.subscribe(() => {
      this.setupSignaturePads();
    });
    this.setupSignaturePads();
  }

  @HostListener('window:resize')
  onResize() {
    this.setupSignaturePads();
  }

  private setupSignaturePads() {
    setTimeout(() => {
      if (!this.signatureFields || !this.signatureWrappers) return;

      const fields = this.signatureFields.toArray();
      const wrappers = this.signatureWrappers.toArray();

      fields.forEach((sigComp, index) => {
        try {
          if (!sigComp || !sigComp.signaturePad) return;
          const pad = sigComp.signaturePad;
          const canvas = pad.canvas as HTMLCanvasElement;
          const wrapperEl = wrappers[index]?.nativeElement;

          if (wrapperEl) {
            // 1. Force Red Ink and White Background
            pad.set('penColor', 'rgb(255, 0, 0)');
            pad.set('backgroundColor', 'rgb(255, 255, 255)'); 

            // Strip inner container borders to eliminate double box
            let currentElement = canvas.parentElement;
            while (currentElement && currentElement !== wrapperEl) {
              currentElement.style.setProperty('border', 'none', 'important');
              currentElement.style.setProperty('background', 'transparent', 'important');
              currentElement = currentElement.parentElement;
            }

            // Target Dimensions (smaller height as requested)
            const targetWidth = wrapperEl.clientWidth;
            const targetHeight = 130; 

            if (targetWidth > 0) {
              const ratio = Math.max(window.devicePixelRatio || 1, 1);
              const expectedWidth = targetWidth * ratio;

              // Only resize if the dimensions are off, preventing infinite clearing
              if (canvas.width !== expectedWidth || canvas.height !== targetHeight * ratio) {
                const data = pad.toData(); 

                // Set CSS dimensions for the visual layout
                canvas.style.width = targetWidth + 'px';
                canvas.style.height = targetHeight + 'px';

                // Set HTML attributes for the ACTUAL drawing coordinate system (Fixes offset & lag)
                canvas.width = targetWidth * ratio;
                canvas.height = targetHeight * ratio;

                // Scale the internal 2D drawing context for high DPI displays
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  ctx.scale(ratio, ratio);
                }

                // Update signature pad component internals just in case
                try { pad.set('canvasWidth', targetWidth); } catch (e) {}

                // Redraw
                pad.clear(); // Re-applies white background over entire new coordinate space
                if (data && data.length > 0) {
                  pad.fromData(data); 
                }
              }
            }
          }
        } catch (error) {
          console.warn('Canvas configuration handled safely', error);
        }
      });
    }, 200); 
  }

  private getEasternOffsetMs(dateVal: any): number {
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return -(5 * 3600000);
      const utc = new Date(d.toLocaleString('en-US', { timeZone: 'UTC' }));
      const loc = new Date(d.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      return loc.getTime() - utc.getTime();
    } catch (e) {
      return -(5 * 3600000);
    }
  }

  initDynamicForm() {
    this.layoutItems = [];
    this.flatEntities = [];
    this.formValues = {};
    this.listDataMap = {};
    this.documentDataMap = {};
    this.hasSignature = {};
    this.isSignatureModified = {};

    let layoutModel = this.activity?.model?.formLayoutModel;

    if (this.activity?.type === 'consolidated-inspection-activity') {
      layoutModel = this.activity.model?.completeStepActivity?.model?.formLayoutModel;
    }

    if (!layoutModel?.columnEntities) return;

    const entities = [...layoutModel.columnEntities].sort(
      (a: any, b: any) => a.displayOrder - b.displayOrder
    );

    entities.forEach((col: any) => {
      if (col.type === 'table-data-entity-layout') {
        this.layoutItems.push({ type: 'table', rows: col.rows || [] });
        col.rows?.forEach((row: any) => {
          row.cells?.forEach((cell: any) => {
            if (cell.type === 'entity' && cell.entity) {
              this.processEntitySetup(cell.entity);
            }
          });
        });
      } else if (col.entity) {
        this.layoutItems.push({ type: 'field', entity: col.entity });
        this.processEntitySetup(col.entity);
      }
    });
  }

  private processEntitySetup(entity: any) {
    this.flatEntities.push(entity);

    if (entity.dataEntityTypeCode === 'date') {
      let initDate = null;
      if (entity.value) {
        try {
          const parsed = JSON.parse(entity.value);
          if (parsed.Value && !parsed.Value.includes('0001-01-01')) {
            const tempDate = new Date(parsed.Value);
            const offsetMs = this.getEasternOffsetMs(tempDate);
            initDate = new Date(tempDate.getTime() + (tempDate.getTimezoneOffset() * 60000) + offsetMs);
          }
        } catch (e) { }
      }
      this.formValues[entity.$id] = initDate;
    }
    else if (entity.dataEntityTypeCode === 'list-data') {
      const options = (entity.listTextValues || [])
        .filter((o: any) => o.visible !== false)
        .map((o: any) => ({ label: o.text, value: o.value }));

      this.listDataMap[entity.$id] = {
        direction: 'row',
        options: options,
        value: entity.selectedValue || ''
      };
      this.formValues[entity.$id] = entity.selectedValue || '';
    }
    else if (entity.dataEntityTypeCode === 'document' || entity.dataEntityTypeCode === 'inspection-document') {
      this.documentDataMap[entity.$id] = entity.documents || [];
    }
    else if (entity.dataEntityTypeCode === 'signature') {
      this.formValues[entity.$id] = entity.value || null;
      this.hasSignature[entity.$id] = !!entity.value;
    }
    else {
      let initVal = entity.value || '';
      if (typeof initVal === 'string' && initVal.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(initVal);
          initVal = parsed.value !== undefined ? parsed.value : (parsed.calculatedValue !== undefined ? parsed.calculatedValue : initVal);
        } catch (e) {}
      }
      
      const ident = (entity.label || entity.name || '').toLowerCase();
      if (ident.includes('phone') || entity.dataEntityTypeCode === 'phone') {
        initVal = this.formatPhoneNumber(initVal);
      }

      this.formValues[entity.$id] = initVal;
    }
  }

  getFieldWidthClass(field: any): string {
    if (!field) return 'field-half-width';

    if (
      field.dataEntityTypeCode === 'list-data' ||
      field.dataEntityTypeCode === 'rich-text' ||
      field.dataEntityTypeCode === 'document' ||
      field.dataEntityTypeCode === 'inspection-document'
    ) {
      return 'field-full-width';
    }

    return 'field-half-width';
  }

  getPattern(field: any): string {
    const ident = (field.label || field.name || '').toLowerCase();
    
    if (ident.includes('email') || field.dataEntityTypeCode === 'email') {
      return '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';
    }
    
    if (ident.includes('phone') || field.dataEntityTypeCode === 'phone') {
      return '^\\d{3}-\\d{3}-\\d{4}$';
    }

    return '.*'; 
  }

  getMaxLength(field: any): number | undefined {
    const ident = (field.label || field.name || '').toLowerCase();
    if (ident.includes('phone') || field.dataEntityTypeCode === 'phone') {
      return 12; 
    }
    return undefined; 
  }

  getErrorMessage(field: any): string {
    const ident = (field.label || field.name || '').toLowerCase();
    if (ident.includes('email') || field.dataEntityTypeCode === 'email') {
      return 'Please enter valid email';
    }
    if (ident.includes('phone') || field.dataEntityTypeCode === 'phone') {
      return 'Format must be ###-###-####';
    }
    return 'Invalid format';
  }

  // Safely evaluates pattern validity directly from TS to avoid HTML scope bugs
  isFieldInvalid(field: any): boolean {
    const val = this.formValues[field.$id];
    if (val && typeof val === 'string' && val.trim() !== '') {
      const pattern = this.getPattern(field);
      if (pattern && pattern !== '.*') {
        const regex = new RegExp(pattern);
        return !regex.test(val);
      }
    }
    return false;
  }

  formatPhoneNumber(val: string): string {
    if (!val) return '';
    const cleaned = ('' + val).replace(/\D/g, '');
    const truncated = cleaned.substring(0, 10);
    
    const match = truncated.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      let formatted = match[1];
      if (match[2]) formatted += '-' + match[2];
      if (match[3]) formatted += '-' + match[3];
      return formatted;
    }
    return val;
  }

  onTextModelChange(field: any, value: string) {
    if (value === null || value === undefined) {
      this.formValues[field.$id] = value;
      return;
    }

    const ident = (field.label || field.name || '').toLowerCase();
    if (ident.includes('phone') || field.dataEntityTypeCode === 'phone') {
      const formatted = this.formatPhoneNumber(value);
      // Ensures Angular forcefully wipes out typed letters in the DOM sync cycle
      if (value !== formatted) {
        this.formValues[field.$id] = '';
        setTimeout(() => this.formValues[field.$id] = formatted);
      } else {
        this.formValues[field.$id] = formatted;
      }
    } else {
      this.formValues[field.$id] = value;
    }
  }

  onRadioChange(entityId: string, value: string) {
    if (this.listDataMap[entityId]) {
      this.listDataMap[entityId].value = value;
    }
    this.formValues[entityId] = value;
  }

  onSignatureChange(entityId: string, value: any) {
    this.formValues[entityId] = value;
    this.hasSignature[entityId] = !!value;
    this.isSignatureModified[entityId] = true;
  }

  clearSignature(id: string, sigField: any) {
    if (sigField && typeof sigField.clear === 'function') {
      sigField.clear();
    }
    this.formValues[id] = null;
    this.hasSignature[id] = false;
    this.isSignatureModified[id] = true;
  }

  onFileSelected(event: any, field: any) {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    this.uploadingMap[field.$id] = true;
    const uploadCount = files.length;
    let completedCount = 0;

    const uploadEndpoint = `${Utilities.getRootURL()}/api/Document/upload?hasModifiedName=false`;

    Array.from(files).forEach((file: File) => {
      const formData = new FormData();
      formData.append('hasModifiedName', 'false');
      formData.append('file', file, file.name);
      formData.append('filename', file.name);
      formData.append('origName', file.name);

      const pathSegments = [
        this.activity.workflowApplicationId,
        this.activity.id,
        field.templateCode
      ];

      pathSegments.forEach(segment => {
        if (segment) {
          formData.append('parentPath[]', segment);
        }
      });

      this.http.post(uploadEndpoint, formData).subscribe({
        next: (res: any) => {
          if (!this.documentDataMap[field.$id]) {
            this.documentDataMap[field.$id] = [];
          }
          this.documentDataMap[field.$id].push(res);

          completedCount++;
          if (completedCount === uploadCount) {
            this.uploadingMap[field.$id] = false;
            event.target.value = '';
          }
        },
        error: (err) => {
          console.error('File upload error:', err);
          this.toastr.error(`Failed to upload ${file.name}`);
          completedCount++;
          if (completedCount === uploadCount) {
            this.uploadingMap[field.$id] = false;
            event.target.value = '';
          }
        }
      });
    });
  }

  removeDocument(entityId: string, index: number) {
    this.documentDataMap[entityId].splice(index, 1);
  }

  get isFormValid(): boolean {
    if (!this.flatEntities) return true;

    for (const entity of this.flatEntities) {
      if (entity.isRequired) {
        let hasValue = false;
        if (entity.dataEntityTypeCode === 'date') {
          hasValue = !!this.formValues[entity.$id];
        } else if (entity.dataEntityTypeCode === 'list-data') {
          hasValue = !!this.formValues[entity.$id];
        } else if (entity.dataEntityTypeCode === 'document' || entity.dataEntityTypeCode === 'inspection-document') {
          hasValue = this.documentDataMap[entity.$id] && this.documentDataMap[entity.$id].length > 0;
        } else if (entity.dataEntityTypeCode === 'signature') {
          hasValue = !!this.hasSignature[entity.$id];
        } else if (entity.dataEntityTypeCode === 'free-form-text') {
          hasValue = !!this.formValues[entity.$id] && String(this.formValues[entity.$id]).trim() !== '';
        } else {
          hasValue = !!this.formValues[entity.$id];
        }

        if (!hasValue) return false;
      }

      const val = this.formValues[entity.$id];
      if (val && typeof val === 'string' && val.trim() !== '') {
        const pattern = this.getPattern(entity);
        if (pattern && pattern !== '.*') {
          const regex = new RegExp(pattern);
          if (!regex.test(val)) return false; 
        }
      }
    }
    return true;
  }

  onSubmit() {
    if (this.flatEntities) {
      this.flatEntities.forEach((entity: any) => {
        const val = this.formValues[entity.$id];

        if (entity.dataEntityTypeCode === 'date') {
          if (val) {
            const dateObj = new Date(val);
            const offsetMs = this.getEasternOffsetMs(dateObj);
            const realUtcMs = dateObj.getTime() - (dateObj.getTimezoneOffset() * 60000) - offsetMs;
            const iso = new Date(realUtcMs).toISOString();

            const formatted = `${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getDate().toString().padStart(2, '0')}/${dateObj.getFullYear()}`;

            entity.value = JSON.stringify({ Value: iso, HasTime: false, DSTFixed: null });
            entity.formattedValue = formatted;

            if (entity.templateCode === 'COMPLETE_DATE') {
              this.activity.completedOn = iso;
              if (this.activity.model?.inspection) {
                this.activity.model.inspection.completedOn = iso;
              }
            }
          } else {
            entity.value = "";
            entity.formattedValue = "";
          }
        }
        else if (entity.dataEntityTypeCode === 'list-data') {
          entity.selectedValue = val;
          entity.value = val;
        }
        else if (entity.dataEntityTypeCode === 'document' || entity.dataEntityTypeCode === 'inspection-document') {
          entity.documents = this.documentDataMap[entity.$id] || [];
          entity.value = JSON.stringify(entity.documents);
        }
        else if (entity.dataEntityTypeCode === 'signature') {
          if (this.isSignatureModified[entity.$id] || !entity.value) {
            if (val) {
              entity.value = typeof val === 'string' ? val : JSON.stringify(val);
              try {
                const parsed = typeof val === 'string' ? JSON.parse(val) : val;
                entity.formattedValue = parsed.image || "";
              } catch (e) {
                entity.formattedValue = val;
              }
            } else {
              entity.value = "";
              entity.formattedValue = "";
            }
          }
        }
        else {
          const originalValue = entity.value;
          if (typeof originalValue === 'string' && originalValue.trim().startsWith('{')) {
            try {
              const parsed = JSON.parse(originalValue);
              parsed.value = val || '';
              parsed.editedValue = val || '';
              parsed.calculatedValue = val || '';
              entity.value = JSON.stringify(parsed);
            } catch (e) {
              entity.value = val;
            }
          } else {
             entity.value = val;
          }

          if (entity.templateCode === 'COMPLETE_SDL_NOTES') {
            this.activity.notes = val;
          }
        }
      });
    }

    this.prepareConsolidatedSubmittal(3);
    this.confirm.emit(this.activity);
  }

  private prepareConsolidatedSubmittal(submittedStep: number): void {
    if (this.activity?.type !== 'consolidated-inspection-activity') return;

    const requestColumns = this.activity.model?.requestStepActivity?.model?.formLayoutModel?.columnEntities ?? [];
    const scheduleColumns = this.activity.model?.scheduleStepActivity?.model?.formLayoutModel?.columnEntities ?? [];
    const completeColumns = this.activity.model?.completeStepActivity?.model?.formLayoutModel?.columnEntities ?? [];

    this.activity.submittedStep = submittedStep;
    if (this.activity.model?.formLayoutModel) {
      this.activity.model.formLayoutModel.columnEntities = [
        ...requestColumns,
        ...scheduleColumns,
        ...completeColumns
      ];
    }

    this.activity.model.requestStepActivity = null;
    this.activity.model.scheduleStepActivity = null;
    this.activity.model.completeStepActivity = null;
  }

  onCancel() { this.cancel.emit(); }
}
